import Page from './Page';
import Component from 'vue-class-component';
import TabBar from './TabBar.vue';
import HeaderComponent from './Header.vue';
import {
    Stripe,
    StripeElements,
    Token,
    loadStripe,
    PaymentMethod,
} from '@stripe/stripe-js';
import Pointer from './Pointer.vue';
import { CacheAsync } from '../common/Decorators/CacheAsync';
import template from './Billing.vue.html';

const style = {
    base: {
        iconColor: '#666EE8',
        color: '#333333',
        fontSize: '1.2em',
        fontFamily: 'Yu Gothic',
        '::placeholder': {
            color: '#cccccc',
        },
    },
};

type Bill = {
    id: number;
    item_name: string;
    use_date: string | null;
    unit_price: number;
    quantity: number;
    amount: number;
};
@Component({ template, components: { Pointer, TabBar, HeaderComponent } })
export default class Billing extends Page {
    page = 0;

    stripe: Stripe | null = null;
    elements: StripeElements | null = null;

    token: Token | null = null;
    unpaid: Bill[] = [];
    charges = [];
    breakdown = [];

    paymentMethod: PaymentMethod | null = null;

    finishConfirm: boolean = false;
    isCancellationFee: boolean = false;

    async fetchInfo() {
        const endpoint = this.isCancellationFee
            ? 'fetch-cancellation-info'
            : 'stripe-info';
        const r = await this.app.post(endpoint);
        if (!r.succeed) throw new Error(r.error);
        if (r.account_id) {
            this.stripe = await loadStripe(r.platform_key, {
                stripeAccount: r.account_id,
            });
        } else {
            this.stripe = await loadStripe(r.public_key);
        }

        this.unpaid = r.unpaid || [];
        this.charges = r.charges || [];
        this.breakdown = r.breakdown || [];
    }

    unpaidAmount(): number {
        return this.unpaid.reduce((amount, bill) => amount + bill.amount, 0);
    }

    unpaidList(): Record<string, number> {
        const list = {} as Record<string, number>;
        for (let bill of this.unpaid) {
            const name = bill.item_name || 'Fee';
            if (name in list) list[name] += bill.amount;
            else list[name] = bill.amount;
        }
        return list;
    }

    async created() {
        try {
            this.isCancellationFee = this.app.reserve?.canceled ?? false;

            console.log('Billing created:', {
                reserveCanceled: this.app.reserve?.canceled,
                isCancellationFee: this.isCancellationFee,
            });

            await this.fetchInfo();
            if (this.unpaidAmount()) return this.toCardInput();
            if (this.charges.length) return (this.page = 3);
        } catch (err: any) {
            console.error('Billing error:', err);
            alert(err.message || 'Error');
        }
        this.app.setPage('Terminal');
    }

    @CacheAsync()
    async toCardInput() {
        if (!this.stripe) return this.app.setPage('Terminal');
        this.token = null;
        this.page = 1;

        let locale: 'ja' | 'en' | 'zh' = 'ja';
        switch (this.app.getLang()) {
            case 'en':
                locale = 'en';
                break;
            case 'cn':
                locale = 'zh';
                break;
        }
        this.elements = this.stripe.elements({ locale });
        await this.$nextTick();

        const num = this.elements.create('cardNumber', {
            style,
            showIcon: true,
            iconStyle: 'default',
        });
        num.mount(this.$refs.card as HTMLInputElement);

        const exp = this.elements.create('cardExpiry', { style });
        exp.mount(this.$refs.expiry as HTMLInputElement);

        const cvc = this.elements.create('cardCvc', { style });
        cvc.mount(this.$refs.cvc as HTMLInputElement);
    }

    @CacheAsync()
    async createToken() {
        if (!this.stripe || !this.elements) return this.app.setPage('Terminal');

        const CardNumber = this.elements.getElement('cardNumber');
        console.log(CardNumber);
        if (!CardNumber) return this.toCardInput();
        const result = await this.stripe.createToken(CardNumber);
        if (result.error) {
            console.error(result.error);
            alert(result.error.message);
            return;
        }
        if (!result.token) return;

        this.token = result.token;
        await this.createPaymentMethod();
        this.page = 2;
    }

    @CacheAsync()
    async createPaymentMethod() {
        if (!this.stripe || !this.elements) return this.app.setPage('Terminal');

        const cardElement = this.elements.getElement('cardNumber');
        if (!cardElement) return this.toCardInput();

        const result = await this.stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
        });

        if (result.error) {
            console.error(result.error);
            alert(result.error.message);
            return;
        }
        if (!result.paymentMethod) return;

        this.paymentMethod = result.paymentMethod;
    }

    @CacheAsync()
    async payment() {
        if (!this.token) return;
        const token = this.token.id;
        this.token = null;

        const paymentMethodId = this.paymentMethod?.id;

        try {
            const r = await this.app.post('payment', {
                billings: this.unpaid,
                amount: this.unpaidAmount(),
                token,
                paymentMethodId,
                is_cancellation_fee: this.isCancellationFee,
            });
            if (!r.succeed) throw new Error(r.error);

            if (r.intent?.object === 'payment_intent') {
                this.start3DSecureTimer(r.intent.id);
                await this.stripe
                    ?.confirmCardPayment(r.intent.client_secret, {
                        payment_method: paymentMethodId,
                    })
                    .then(async () => {
                        this.finishConfirm = true;
                        const response = await this.app.post('resolve-intent', {
                            chargeId: r.charge_id,
                            intent_id: r.intent.id,
                            billings: this.unpaid,
                            is_cancellation_fee: this.isCancellationFee,
                        });
                        if (!response.succeed)
                            throw new Error(response.error || r.error);
                    });
            }

            await this.fetchInfo();
            this.page = 3;
        } catch (err: any) {
            alert(this._('retry_card_input'));
            this.app.setPage('Terminal');
        }
    }

    async start3DSecureTimer(intent_id: string) {
        setTimeout(() => {
            if (this.finishConfirm) return;
            this.app.post('three-d-secure-timeout', { intent_id: intent_id });
        }, 300000);
    }
}
